import { Languages } from 'lucide-react';
import { LANG_LABEL, PORTAL_LANGS, useLang, type PortalLang } from '@/lib/lang';

/**
 * The language switch: three small words in the top corner, each in its own
 * language ("עברית", "العربية", "English"), the current one marked. A white pill,
 * so it reads over any building's band. Every word is a 48px target.
 */
export function LangSwitch({
  onChange,
  inline = false,
}: {
  onChange?: (lang: PortalLang) => void;
  /** In the page's flow (at the end of a row) instead of over the top corner: under a short band. */
  inline?: boolean;
}) {
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  return (
    <nav
      aria-label="Language · שפה · اللغة"
      className={
        (inline ? 'ms-auto w-fit ' : 'absolute end-2 top-[max(env(safe-area-inset-top),0.5rem)] z-20 ') +
        'flex items-center rounded-full bg-white/95 px-1 shadow-sm ring-1 ring-slate-200'
      }
      data-testid="lang-switch"
    >
      <Languages className="ms-2 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      {PORTAL_LANGS.map((l) => {
        const current = l === lang;
        return (
          <button
            key={l}
            type="button"
            lang={l}
            dir={l === 'en' ? 'ltr' : 'rtl'}
            aria-pressed={current}
            onClick={() => {
              if (current) return;
              setLang(l);
              // A `?lang=` the page was opened with would undo the choice on reload: drop it.
              const url = new URL(window.location.href);
              if (url.searchParams.has('lang')) {
                url.searchParams.delete('lang');
                window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
              }
              onChange?.(l);
            }}
            className={
              'min-h-[48px] min-w-[48px] px-2 text-[16px] ' +
              (current ? 'font-bold text-brand-900 underline decoration-2 underline-offset-4' : 'text-slate-600')
            }
          >
            {LANG_LABEL[l]}
          </button>
        );
      })}
    </nav>
  );
}
